import type { IPdfCustomReportTemplateListItem } from '@/Interfaces/PdfBuilder/Template.interface';
import { Grid } from '@/components/Grid';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { dateToShortFormat } from '@/helpers/valueFormatter/dateFormatter.helpers';
import GridToolBar from '@/pages/ReportBuilder/Components/GridToolbar';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Button, Stack, buttonClasses } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { gridClasses } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

export type PdfTemplateActionFunction = (template: IPdfCustomReportTemplateListItem) => void;

interface IProps {
  data: IPdfCustomReportTemplateListItem[];
  loading?: boolean;
  onShareTemplate: PdfTemplateActionFunction;
  onDeleteTemplate: PdfTemplateActionFunction;
}

const pillSx = {
  borderRadius: '32px',
  padding: '4px 12px',
  fontSize: '12px',
  [`& .${buttonClasses.startIcon}`]: {
    marginRight: '4px',
  },
};

const getColumns = (
  onShareTemplate: PdfTemplateActionFunction,
  onDeleteTemplate: PdfTemplateActionFunction,
): GridColDef[] => [
  {
    field: 'name',
    flex: 2,
    headerName: 'Template Name',
  },
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Created Date',
    sortable: false,
    valueFormatter: (value: string) => dateToShortFormat(value),
  },
  {
    field: 'owner',
    flex: 2,
    headerName: 'Owner',
    sortable: false,
    valueGetter: (_: unknown, row: IPdfCustomReportTemplateListItem) => row.owner?.fullName ?? row.owner?.email ?? '',
  },
  {
    field: 'id',
    flex: 1.5,
    headerName: 'Action',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    display: 'flex',
    renderCell: ({ row }: GridRenderCellParams<IPdfCustomReportTemplateListItem>) =>
      row?.isOwner ? (
        <Stack direction='row' spacing={1}>
          <Button variant='outlined' onClick={() => onShareTemplate(row)} sx={pillSx}>
            Share
          </Button>
          <Button variant='outlined' onClick={() => onDeleteTemplate(row)} sx={pillSx}>
            Delete
          </Button>
        </Stack>
      ) : null,
  },
];

const PdfTemplateReportsGrid = ({ data, loading, onShareTemplate, onDeleteTemplate }: IProps) => {
  const columns = useMemo(() => getColumns(onShareTemplate, onDeleteTemplate), [onShareTemplate, onDeleteTemplate]);

  return (
    <Grid
      sx={{
        border: 'none',
        [`& .${gridClasses.main}`]: {
          borderBottom: '1px solid rgb(227, 227, 227)',
        },
        [`& .${gridClasses.columnHeaders}`]: {
          borderRadius: 0,
          borderTop: '1px solid rgb(227, 227, 227)',
        },
      }}
      columns={columns}
      loading={loading}
      autosizeOnMount
      autosizeOptions={EXPAND_OPTIONS}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      showCellVerticalBorder
      showColumnVerticalBorder
      rows={data}
      columnHeaderHeight={30}
      slots={{
        // @ts-expect-error GridToolBar accepts custom loading/title props not in GridToolbarProps
        toolbar: GridToolBar,
        columnSortedAscendingIcon: KeyboardArrowUp,
        columnSortedDescendingIcon: KeyboardArrowDown,
      }}
      slotProps={{
        toolbar: {
          // @ts-expect-error loading prop not in ToolbarPropsOverrides
          loading,
          title: 'Template Reports',
        },
        footer: {
          className: 'relative',
        },
      }}
    />
  );
};

export default PdfTemplateReportsGrid;
